import { Journey } from '@audibene/ta.utils-opportunity'
import Joi from 'joi'
import { AppointmentType, Country } from '../../../constants'
import { AppointmentFormData } from '../../../types'

enum Maximum {
  DURATION_IN_MINUTES = 180,
}

enum Minimum {
  DURATION_IN_MINUTES = 1,
}

const country = Joi.string().valid(...Object.values(Country))
const date = Joi.date()
const journey = Joi.string().valid(...Object.values(Journey))
const number = Joi.number()
const string = Joi.string()
const nullableString = string.allow(null, '')
const type = Joi.string().valid(...Object.values(AppointmentType))

// Gated on acfuRequiresExtendedWindowReason (derived from the actually selected date — see
// useAcfuProgressiveDisclosure) rather than acfuVisibleWindowDays (window-expansion state): a
// user who expands the window and then picks a date still within the 3-day default shouldn't be
// asked for a reason. A plain boolean `is: true` correctly falls through to `otherwise` when the
// field is undefined/false, unlike the numeric comparison this replaced.
const acfuExtendedWindowReason = Joi.any().when('acfuRequiresExtendedWindowReason', {
  is: true,
  then: string.required().messages({
    // 'string.base' covers the untouched-field case (value is null, not ''), 'string.empty'
    // covers a field that was touched and cleared back out.
    'string.base': 'Required when booking a date beyond the default 3-day window',
    'string.empty': 'Required when booking a date beyond the default 3-day window',
  }),
  otherwise: nullableString.optional(),
})

const validationSchema = Joi.object<AppointmentFormData>({
  acfuExtendedWindowReason,
  acfuRequiresExtendedWindowReason: Joi.boolean().optional(),
  acfuShowMoreClickCount: number.optional(),
  acfuVisibleWindowDays: number.optional(),
  clientStartDate: date.required().messages({
    'date.base': 'Please select a day',
  }),
  clientStartTime: date
    .messages({
      'date.base': 'Please enter a valid time',
    })
    .when('isManualMode', { is: false, then: date.required() }),
  manualStartTime: date
    .messages({
      'date.base': 'Please enter a valid time',
    })
    .when('isManualMode', { is: true, then: date.required() }),
  isManualMode: Joi.boolean().default(false),
  country: country.required(),
  customer_salesforce_id: string.required(),
  customer_timezone: string.required().messages({
    'string.base': 'Please select the customer time zone',
  }),
  description: nullableString.optional(),
  duration_in_minutes: number.max(Maximum.DURATION_IN_MINUTES).min(Minimum.DURATION_IN_MINUTES).required(),
  duration_in_minutes_temp: number.required(),
  eligibleCabinIds: Joi.array().items(string).optional(),
  eligibleResourcePairs: Joi.array().items(Joi.array().items(string).min(1).max(2)).optional(),
  fitter_timezone: nullableString.optional(),
  isCabinSelectionDisabled: Joi.boolean().optional(),
  isCombinedBooking: Joi.boolean().optional(),
  journey: journey.required(),
  opportunity_gid: string.required(),
  resource_ids: Joi.array().items(string),
  specialist_id: string.required(),
  month_date: date.optional(),
  start_date: date.required(),
  state: string.optional(),
  title: string.required(),
  type: type.required().messages({
    'any.only': 'Please select an appointment type',
  }),
}).required()

export { validationSchema }
